"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState } from "react";
import { VerticalTabBar } from "@/components/ui/tabs";
import { TextBlockContent } from "./Text";
import { ContentCard, type ContentCardProps } from "@/components/ui/cards/contentCard";
import { SlideUpInOut } from "@/components/animations";
import type { ThemeVariant, ContentCardItem, IconName } from "@/types";
import { AnimatePresence, motion } from "motion/react";
interface Tab {
  id: string;
  label: string;
  icon?: IconName;
  headline: string;
  text: string;
  cards: ContentCardItem[];
}
export interface VerticalTabsProps {
  tabs: Tab[];
  variant?: ThemeVariant;
  className?: string;
}

/**
 * VerticalTabs component
 * Displays a vertical tab bar on the left (4 cols) and content on the right (8 cols)
 */
export const VerticalTabs: React.FC<VerticalTabsProps> = t0 => {
  const $ = _c(32);
  const {
    tabs,
    variant: t1,
    className: t2
  } = t0;
  const variant = t1 === undefined ? "light" : t1;
  const className = t2 === undefined ? "" : t2;
  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id || "");
  let activeTab;
  let column1Cards;
  let t3;
  let t4;
  if ($[0] !== activeTabId || $[1] !== tabs) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      let t5;
      if ($[6] !== activeTabId) {
        t5 = t => t.id === activeTabId;
        $[6] = activeTabId;
        $[7] = t5;
      } else {
        t5 = $[7];
      }
      activeTab = tabs.find(t5);
      if (!activeTab || tabs.length === 0) {
        t4 = null;
        break bb0;
      }
      column1Cards = (activeTab.cards || []).filter(_temp);
      t3 = (activeTab.cards || []).filter(_temp2);
    }
    $[0] = activeTabId;
    $[1] = tabs;
    $[2] = activeTab;
    $[3] = column1Cards;
    $[4] = t3;
    $[5] = t4;
  } else {
    activeTab = $[2];
    column1Cards = $[3];
    t3 = $[4];
    t4 = $[5];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  const column2Cards = t3;
  const t5 = `py-xl ${className}`;
  let t6;
  if ($[8] !== tabs) {
    t6 = tabs.map(_temp3);
    $[8] = tabs;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== activeTabId || $[11] !== t6 || $[12] !== variant) {
    t7 = <aside className="col-span-full lg:col-span-4"><VerticalTabBar tabs={t6} activeTabId={activeTabId} variant={variant} onTabChange={setActiveTabId} /></aside>;
    $[10] = activeTabId;
    $[11] = t6;
    $[12] = variant;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== activeTab.headline || $[15] !== activeTab.text || $[16] !== variant) {
    t8 = <TextBlockContent headingTag="h3" headline={activeTab.headline} text={activeTab.text} variant={variant} />;
    $[14] = activeTab.headline;
    $[15] = activeTab.text;
    $[16] = variant;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== activeTab.cards || $[19] !== activeTabId || $[20] !== column1Cards || $[21] !== column2Cards) {
    t9 = activeTab.cards && activeTab.cards.length > 0 && <div className="grid grid-cols-1 md:grid-cols-2 gap-md"><VerticalTabColumn id={`${activeTabId}-col1`} cards={column1Cards} /><VerticalTabColumn id={`${activeTabId}-col2`} cards={column2Cards} /></div>;
    $[18] = activeTab.cards;
    $[19] = activeTabId;
    $[20] = column1Cards;
    $[21] = column2Cards;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8 || $[24] !== t9) {
    t10 = <main className="col-span-full lg:col-span-8 flex flex-col gap-lg">{t8}{t9}</main>;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t7) {
    t11 = <div className="container grid grid-cols-12 gap-lg">{t7}{t10}</div>;
    $[26] = t10;
    $[27] = t7;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== t11 || $[30] !== t5) {
    t12 = <section className={t5}>{t11}</section>;
    $[29] = t11;
    $[30] = t5;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
interface VerticalTabColumnProps {
  id: string;
  cards: ContentCardItem[];
}
const VerticalTabColumn: React.FC<VerticalTabColumnProps> = t0 => {
  const $ = _c(8);
  const {
    id,
    cards
  } = t0;
  let t1;
  if ($[0] !== cards || $[1] !== id) {
    let t2;
    if ($[3] !== id) {
      t2 = (card, index) => <AnimatePresence propagate={true} key={index}><motion.div key={`${id}-${index}`} initial={{
          opacity: 0
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} transition={{
          layout: {
            type: "spring",
            stiffness: 200,
            damping: 30
          },
          opacity: {
            duration: 0.2
          }
        }}><SlideUpInOut key={`${id}-${index}-slide`}><ContentCard key={`${id}-${index}-card`} {...card as ContentCardProps} color="purple-medium" size="small" /></SlideUpInOut></motion.div></AnimatePresence>;
      $[3] = id;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = cards.map(t2);
    $[0] = cards;
    $[1] = id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[5] !== id || $[6] !== t1) {
    t2 = <AnimatePresence mode="wait"><div key={id} className="flex flex-col gap-md justify-center">{t1}</div></AnimatePresence>;
    $[5] = id;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
function _temp(_, index) {
  return index % 2 === 0;
}
function _temp2(__0, index_0) {
  return index_0 % 2 === 1;
}
function _temp3(tab) {
  return {
    id: tab.id,
    label: tab.label,
    icon: tab.icon
  };
}